// Binary frame codec for the cmx wire protocol (docs/protocol.md).
//
// Frame: u8 type | u32 session id (BE) | payload

import type { NetReport } from './quality';

export const T = {
  INPUT: 0x01,
  RESIZE: 0x02,
  ATTACH: 0x03,
  DETACH: 0x04,
  ACK: 0x05,
  PING: 0x06,
  CONTROL: 0x07,
  /** v2: INPUT with a per-stream sequence number (durable input, see docs/protocol.md). */
  INPUT_SEQ: 0x08,
  OUTPUT: 0x81,
  SNAPSHOT: 0x82,
  EXITED: 0x83,
  /** v2: highest INPUT_SEQ applied for a stream. */
  INPUT_ACK: 0x84,
  PONG: 0x86,
  CONTROL_REPLY: 0x87,
  EVENT: 0x88,
} as const;

export const HEADER = 5;
/** ATTACH offset meaning "I have nothing, send a snapshot". */
export const NO_OFFSET = 0xffff_ffff_ffff_ffffn;

/** Running, needs you, done (output nobody has seen since), idle, or its program exited. */
export type SessionStatus = 'working' | 'waiting' | 'done' | 'idle' | 'exited';

export interface Session {
  id: number;
  name: string;
  command: string;
  created: string;
  status: SessionStatus;
  title: string;
  exitCode: number | null;
  /** What runs in it: a coding agent by name ("Claude Code"), else the command's name; none at the prompt. */
  program?: string;
  /** While waiting: what the program said when it asked for you. */
  notice?: string;
  /** The latest of output, input and creation. */
  lastActivity?: string;
  /** The folder its shell is in, the top folder of the git repository holding it, and its branch. */
  cwd?: string;
  root?: string;
  branch?: string;
  /** Where the name came from: a rename, the agent's own title, an AI name, the folder (agent names.go). */
  nameSource?: 'user' | 'agent' | 'ai' | 'default';
  /** How lists show it, worked out by the machine (proto/rows.go): clients only bucket and draw. */
  row?: SessionRow;
  /** Its place in every list, 0 first: what needs you, runs, is done, then the rest (agent lifecycle.go). */
  rank?: number;
  /** How many clients have it open; how many of them are looking at it (agent viewing.go). */
  attached?: number;
  viewing?: number;
  /** Its state's words and the time they count from (proto/state.go; ui/time.ts says it). */
  state?: { label: string; since?: string };
}

/** A session that closed on its own (agent lifecycle.go): kept a week; reopening brings it back as a new one. */
export interface ClosedSession {
  id: number;
  name: string;
  program?: string;
  cwd?: string;
  row?: SessionRow;
  lastActivity: string;
  closedAt: string;
}

/** A session's groups and second line by organize mode (spec "Sessions: order, groups, leaving and cleanup"). */
export interface SessionRow {
  /** A coding agent the machine recognises: its own icon, grouped by its name. */
  agent: boolean;
  groups: Record<'folder' | 'program', { key: string; label: string }>;
  meta: Record<'none' | 'folder' | 'program', { program?: string; where?: string; branch?: string }>;
  /** Program · the folder's short path · branch, for a tooltip; '' before the machine knows. */
  place: string;
}

export interface Port {
  port: number;
  process: string;
  address: string;
}

// ---- what runs on the machine (control op "activity", agent activity.go / desktop_nav.go) ----

/** One of the user's processes, busiest first. */
export interface MachineProcess {
  pid: number;
  name: string;
  command: string;
  /** % of one core since the last look. */
  cpu: number;
  /** Resident bytes. */
  memory: number;
  /** Unix seconds. */
  started: number;
  /** The cmx session it runs in. */
  session?: number;
}

/** A window open on the machine's desktop. */
export interface DesktopWindow {
  id: string;
  title: string;
  app: string;
  pid?: number;
  /** In front. */
  active: boolean;
  /** A small PNG as a data URL, when the app has one. */
  icon?: string;
}

/** Where a workspace's panes sit (tabs.ts Layout): a pane, or a split of two or more sized as fractions. */
export type LayoutNode = { pane: number } | { dir: 'row' | 'col'; sizes: number[]; kids: LayoutNode[] };

/** A row's workspace as the machine keeps it (docs/protocol.md "Workspaces on the machine"): its panes of tabs (no
 *  blank tabs), the pane focused, its split tree, and its version (`ver`, what a put says it last heard). `sess`,
 *  `index`, `name`, `zoomed` and `made` are the machine's (tmux's session, window index and name). */
export interface LayoutGroup {
  id: number;
  lead: number;
  panes: { tabs: number[]; active: number }[];
  focused: number;
  layout: LayoutNode;
  ver: number;
  sess?: number;
  index?: number;
  name?: string;
  zoomed?: boolean;
  /** "agent": the tmux commands made it (a row an agent started). */
  made?: 'agent';
}

/** Every row's workspace, whole (the `layout` event and op). `seeded`: an app's own workspaces were taken in. */
export interface MachineLayout {
  version: number;
  groups: LayoutGroup[];
  sessions?: { id: number; name?: string; created: number }[];
  seeded?: boolean;
}

export interface MachineActivity {
  processes: MachineProcess[];
  windows: DesktopWindow[];
  ports: Port[];
}

export type ServerEvent =
  | { kind: 'hello'; version: number; features?: string[] }
  | { kind: 'sessions'; sessions: Session[]; closed?: ClosedSession[]; now?: string }
  | { kind: 'ports'; ports: Port[] }
  /** A paused output stream resumes: if a snapshot comes next, it skips ahead (keep scrollback). */
  | { kind: 'resync'; session: number }
  /** `cmx detach` was typed in the session from this tab: leave it, back to the list. */
  | { kind: 'detach'; session: number }
  | { kind: 'clipboard'; text?: string; image?: string }
  | { kind: 'notify'; session: number; title?: string; body?: string }
  | { kind: 'desktop'; running: boolean }
  | { kind: 'desktop-control'; controller: string }
  /** The workspaces, whole: when this app starts following them (caps `layout`) and on every change. */
  | { kind: 'layout'; layout: MachineLayout }
  /** Input reached the session through the API (an agent typing: `cmx tmux`, `cmx send`), at most every 500 ms
   *  while it lasts; `by` says who ("Claude Code", "an agent"). */
  | { kind: 'driven'; session: number; by: string }
  /** The desktop stream on a bad network (agent desknet.go): 30 fps while the link can't keep up, then a
   *  smaller picture of the same desktop (scale: its share of the desktop's size, 1 when whole). */
  | { kind: 'desktop-net'; mode: 'normal' | 'low'; policy: 'auto' | 'full' | 'low'; fps: number; scale: number; kbps: number; queueMs: number; loss: number }
  /** From the hub, every 5 s: both legs of this connection (net/quality.ts). */
  | NetReport
  /** The machine's load as its agent judges it (internal/loadq), in its words: sent when it changes. */
  | ({ kind: 'machine-load' } & MachineLoad)
  | {
      kind: 'open-url';
      id: string;
      session: number;
      url: string;
      /** laptop: open in this browser; preview: a dev server on the machine; machine: already opened there. */
      target: 'laptop' | 'preview' | 'machine';
      /** link: nothing of the machine's; server: a server on its localhost; signin: calls back to one. */
      reason?: 'link' | 'server' | 'signin';
      app?: string;
      port?: number;
      path?: string;
      /** The CLI attached with auto-forward made this callback port a localhost port on its computer and opened the link there. */
      forward?: number;
    }
  /** A sign-in opened in the machine's browser went through (its callback server closed). */
  | { kind: 'open-url-done'; id: string; session: number }
  /** The login form focused in the machine's browser, in display pixels (none: no fields). */
  | LoginFields;

/** agent loginfields.go: what the login-field overlay draws, and fills through "desktop.fill". */
export interface LoginFields {
  kind: 'login-fields';
  /** The tab, for desktop.fill. */
  target: string;
  /** The tab's host, as the machine's browser knows it. */
  site: string;
  screen: [number, number];
  fields: LoginField[] | null;
  focused: string;
}

export interface LoginField {
  id: string;
  kind: 'username' | 'password' | 'new-password';
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface ControlReply {
  id: number;
  ok: boolean;
  result?: unknown;
  error?: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

function frame(type: number, sid: number, payloadLen: number): { buf: Uint8Array; dv: DataView } {
  const buf = new Uint8Array(HEADER + payloadLen);
  const dv = new DataView(buf.buffer);
  dv.setUint8(0, type);
  dv.setUint32(1, sid >>> 0);
  return { buf, dv };
}

/** INPUT: string data is UTF-8 encoded straight into the frame (no intermediate copy). */
export function encInput(sid: number, data: string | Uint8Array): Uint8Array {
  if (typeof data === 'string') {
    // Worst case 3 bytes per UTF-16 code unit.
    const tmp = new Uint8Array(HEADER + data.length * 3);
    const { written } = enc.encodeInto(data, tmp.subarray(HEADER));
    const buf = tmp.subarray(0, HEADER + written);
    const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
    dv.setUint8(0, T.INPUT);
    dv.setUint32(1, sid >>> 0);
    return buf;
  }
  const { buf } = frame(T.INPUT, sid, data.length);
  buf.set(data, HEADER);
  return buf;
}

/** INPUT_SEQ: u64 stream, u64 seq, then the bytes (UTF-8 for strings). */
export function encInputSeq(sid: number, stream: bigint, seq: number, data: string | Uint8Array): Uint8Array {
  const P = HEADER + 16;
  let buf: Uint8Array;
  if (typeof data === 'string') {
    const tmp = new Uint8Array(P + data.length * 3);
    const { written } = enc.encodeInto(data, tmp.subarray(P));
    buf = tmp.subarray(0, P + written);
  } else {
    buf = new Uint8Array(P + data.length);
    buf.set(data, P);
  }
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  dv.setUint8(0, T.INPUT_SEQ);
  dv.setUint32(1, sid >>> 0);
  dv.setBigUint64(HEADER, stream);
  dv.setBigUint64(HEADER + 8, BigInt(seq));
  return buf;
}

/** The bytes of an INPUT_SEQ frame re-framed as plain INPUT (v1 agents). */
export function inputSeqToInput(frame: Uint8Array): Uint8Array {
  const dv = new DataView(frame.buffer, frame.byteOffset, frame.byteLength);
  return encInput(dv.getUint32(1), frame.subarray(HEADER + 16));
}

export function encResize(sid: number, cols: number, rows: number): Uint8Array {
  const { buf, dv } = frame(T.RESIZE, sid, 4);
  dv.setUint16(HEADER, cols);
  dv.setUint16(HEADER + 2, rows);
  return buf;
}

function encU64(type: number, sid: number, v: bigint): Uint8Array {
  const { buf, dv } = frame(type, sid, 8);
  dv.setBigUint64(HEADER, v);
  return buf;
}

export const encAttach = (sid: number, offset: bigint) => encU64(T.ATTACH, sid, offset);
export const encAck = (sid: number, offset: bigint) => encU64(T.ACK, sid, offset);
export const encDetach = (sid: number) => frame(T.DETACH, sid, 0).buf;

export function encPing(ts: number): Uint8Array {
  const { buf, dv } = frame(T.PING, 0, 8);
  dv.setFloat64(HEADER, ts);
  return buf;
}

export function encControl(req: object): Uint8Array {
  const body = enc.encode(JSON.stringify(req));
  const { buf } = frame(T.CONTROL, 0, body.length);
  buf.set(body, HEADER);
  return buf;
}

export type Decoded =
  | { type: typeof T.OUTPUT; sid: number; offset: bigint; data: Uint8Array }
  | { type: typeof T.SNAPSHOT; sid: number; offset: bigint; cols: number; rows: number; data: Uint8Array }
  | { type: typeof T.EXITED; sid: number; code: number }
  | { type: typeof T.INPUT_ACK; sid: number; stream: bigint; seq: number }
  | { type: typeof T.PONG; sid: number; ts: number }
  | { type: typeof T.CONTROL_REPLY; sid: number; reply: ControlReply }
  | { type: typeof T.EVENT; sid: number; event: ServerEvent }
  | { type: 'unknown'; sid: number; raw: number };

/** Decode one frame. `buf` is a view of exactly one frame; returned data views alias it. */
export function decode(buf: Uint8Array): Decoded | null {
  if (buf.length < HEADER) return null;
  const dv = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
  const type = dv.getUint8(0);
  const sid = dv.getUint32(1);
  const p = HEADER;
  switch (type) {
    case T.OUTPUT:
      if (buf.length < p + 8) return null;
      return { type, sid, offset: dv.getBigUint64(p), data: buf.subarray(p + 8) };
    case T.SNAPSHOT:
      if (buf.length < p + 12) return null;
      return {
        type,
        sid,
        offset: dv.getBigUint64(p),
        cols: dv.getUint16(p + 8),
        rows: dv.getUint16(p + 10),
        data: buf.subarray(p + 12),
      };
    case T.EXITED:
      return { type, sid, code: buf.length >= p + 4 ? dv.getInt32(p) : 0 };
    case T.INPUT_ACK:
      if (buf.length < p + 16) return null;
      return { type, sid, stream: dv.getBigUint64(p), seq: Number(dv.getBigUint64(p + 8)) };
    case T.PONG:
      return { type, sid, ts: buf.length >= p + 8 ? dv.getFloat64(p) : NaN };
    case T.CONTROL_REPLY:
      return { type, sid, reply: JSON.parse(dec.decode(buf.subarray(p))) as ControlReply };
    case T.EVENT:
      return { type, sid, event: JSON.parse(dec.decode(buf.subarray(p))) as ServerEvent };
    default:
      return { type: 'unknown', sid, raw: type };
  }
}

/** Busy (things slow down) or full (memory or the disk nearly out), and what every app says about it. */
export interface MachineLoad {
  state: 'ok' | 'busy' | 'full';
  why?: 'memory' | 'cpu' | 'disk';
  title?: string;
  detail?: string;
}
